"use client";

/*
 * The elevation profile of a trail's track, drawn as an SVG. A port of the
 * current app's models/elevationProfile.js (the same file lives in admin-ui and,
 * as a third copy, in admin-api's field sheet): the three are meant to draw the
 * same thing, so a change to one wants making in the others.
 */
import { useId } from "react";
import type { TrackPoint } from "@/lib/types/event";

const EARTH_RADIUS = 6371000;
const radians = (degrees: number) => (degrees * Math.PI) / 180;

// The drawing's own coordinates. It is stretched to whatever box it is put in --
// height against distance is a graph, not a picture -- so these are proportions
// rather than pixels, and every line is drawn with a width that does not stretch
const WIDTH = 1000;
const HEIGHT = 260;
const PADDING = { top: 14, right: 14, bottom: 14, left: 14 };

// as many columns as the shape needs: a recorded track runs to thousands of points
const MAX_COLUMNS = 400;

// a GPS's elevation wanders a metre or two at a standstill, so a change is only
// counted once it has gone further than that
const ELEVATION_NOISE = 3;

// and from one point to the next a few metres on it jumps by more than that, so
// each height is first averaged with the others within this many metres along
// the track, as Wikiloc and gpx.studio do: the back-office's figures are counted
// the same way
const SMOOTHING = 50;

// the least height the drawing's side stands for, in metres (this app's copy
// only): a trail that climbs less fills only its share of the box, so a walk on
// the flat draws flat. Of the trails in use, half have under 165 m between their
// lowest and highest points; at 150 the boardwalks are a band along the bottom
// and a trail of short, repeated climbs keeps most of its shape
const MIN_RANGE = 150;

// how far apart the gridlines are, in metres: the first that leaves four or fewer
const GRID_STEPS = [10, 20, 25, 50, 100, 200, 250, 500, 1000];

/**
 * How steep a climb is, in colour: green on the flat through yellow and orange
 * to red on anything over a quarter. Only uphill is coloured, so the flat and
 * every descent are the same green.
 */
const SLOPE_COLOURS: [number, [number, number, number]][] = [
  [0, [67, 160, 71]],
  [6, [156, 204, 101]],
  [10, [253, 216, 53]],
  [15, [255, 152, 0]],
  [20, [244, 81, 30]],
  [25, [198, 40, 40]],
];
const SLOPE_WINDOW = 60;

// the words under a profile for what its colours say: each a slope, as a
// percentage, drawn in the colour the ground has there
const SLOPE_LABELS: [number, string][] = [
  [0, "Plano ou a descer"],
  [10, "Subida suave"],
  [15, "Subida moderada"],
  [25, "Subida íngreme"],
];

const COLOURS = { line: "#1f2a24", grid: "#e6e9e7", here: "#2f7fd6" };

type SeriesPoint = { distance: number; elevation: number };

/** metres between two [latitude, longitude] points over the surface of the earth */
export const distanceBetween = ([lat1, lon1]: TrackPoint, [lat2, lon2]: TrackPoint) => {
  const a = Math.sin(radians(lat2 - lat1) / 2) ** 2 + Math.cos(radians(lat1)) * Math.cos(radians(lat2)) * Math.sin(radians(lon2 - lon1) / 2) ** 2;
  return 2 * EARTH_RADIUS * Math.asin(Math.sqrt(a));
};

/**
 * The track as distance walked against height, in metres, for the points that
 * have a height. Distance still counts the points with no height, so a gap in
 * the recording does not pull the rest of the walk back.
 */
export const elevationSeries = (track: TrackPoint[] | null | undefined): SeriesPoint[] => {
  const points = (track || []).filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  const series: SeriesPoint[] = [];
  let distance = 0;
  points.forEach((point, i) => {
    if (i) {
      distance += distanceBetween(points[i - 1], point);
    }
    if (typeof point[2] === "number" && Number.isFinite(point[2])) {
      series.push({ distance, elevation: point[2] });
    }
  });
  return series;
};

/**
 * Each height of an elevation series averaged with the others within SMOOTHING
 * metres along the track, in the same order: what the climbing is counted on.
 * Points further apart than that are left as they were recorded.
 */
export const smoothHeights = (series: SeriesPoint[]): number[] => {
  let from = 0;
  let to = 0;
  let sum = 0;
  return series.map(({ distance }) => {
    while (to < series.length && series[to].distance <= distance + SMOOTHING / 2) {
      sum += series[to].elevation;
      to++;
    }
    while (series[from].distance < distance - SMOOTHING / 2) {
      sum -= series[from].elevation;
      from++;
    }
    return sum / (to - from);
  });
};

/**
 * What the profile says in figures: how far it goes, how much of it is up and
 * how much down, and the highest and lowest it gets. Null when the track has no
 * heights to read.
 */
export const elevationStats = (track: TrackPoint[] | null | undefined) => {
  const series = elevationSeries(track);
  if (series.length < 2) {
    return null;
  }
  let ascent = 0;
  let descent = 0;
  const smoothed = smoothHeights(series);
  let settled = smoothed[0];
  smoothed.forEach((elevation) => {
    if (elevation - settled >= ELEVATION_NOISE) {
      ascent += elevation - settled;
      settled = elevation;
    } else if (settled - elevation >= ELEVATION_NOISE) {
      descent += settled - elevation;
      settled = elevation;
    }
  });
  const heights = series.map((p) => p.elevation);
  return {
    distance: series[series.length - 1].distance,
    ascent: Math.round(ascent),
    descent: Math.round(descent),
    min: Math.round(Math.min(...heights)),
    max: Math.round(Math.max(...heights)),
  };
};

// one point per column, each the highest of the points falling in it, so a
// summit between two columns is not the one point that goes missing
const columns = (series: SeriesPoint[], count: number) => {
  const last = series[series.length - 1].distance;
  if (!last) {
    return [series[0], series[series.length - 1]];
  }
  const step = last / count;
  const picked: SeriesPoint[] = [];
  let column: SeriesPoint | null = null;
  let index = -1;
  series.forEach((point) => {
    const at = Math.min(count - 1, Math.floor(point.distance / step));
    if (at !== index) {
      if (column) {
        picked.push(column);
      }
      index = at;
      column = point;
    } else if (column && point.elevation > column.elevation) {
      column = point;
    }
  });
  if (column) {
    picked.push(column);
  }
  const end = series[series.length - 1];
  if (picked[picked.length - 1] !== end) {
    picked.push(end);
  }
  return picked;
};

const gridLines = (low: number, high: number) => {
  const step = GRID_STEPS.find((candidate) => (high - low) / candidate <= 4) || GRID_STEPS[GRID_STEPS.length - 1];
  const lines: number[] = [];
  for (let height = Math.ceil(low / step) * step; height <= high; height += step) {
    lines.push(height);
  }
  return lines;
};

/** how steep it is where a point sits, uphill and as a percentage, over the stretch around it */
const slopeAt = (points: SeriesPoint[], i: number) => {
  let from = i;
  let to = i;
  while (from > 0 && points[i].distance - points[from].distance < SLOPE_WINDOW / 2) {
    from--;
  }
  while (to < points.length - 1 && points[to].distance - points[i].distance < SLOPE_WINDOW / 2) {
    to++;
  }
  const run = points[to].distance - points[from].distance;
  if (run <= 0) {
    return 0;
  }
  return Math.max(0, ((points[to].elevation - points[from].elevation) / run) * 100);
};

const slopeColour = (slope: number): [number, number, number] => {
  const last = SLOPE_COLOURS[SLOPE_COLOURS.length - 1];
  if (slope >= last[0]) {
    return last[1];
  }
  for (let i = 1; i < SLOPE_COLOURS.length; i++) {
    const [toSlope, toColour] = SLOPE_COLOURS[i];
    if (slope > toSlope) {
      continue;
    }
    const [fromSlope, fromColour] = SLOPE_COLOURS[i - 1];
    const part = (slope - fromSlope) / (toSlope - fromSlope);
    return fromColour.map((channel, c) => Math.round(channel + (toColour[c] - channel) * part)) as [number, number, number];
  }
  return SLOPE_COLOURS[0][1];
};

/** the stops of the gradient the ground is painted with, laid along the drawing; a run of one colour keeps only its ends */
const slopeStops = (points: SeriesPoint[]) => {
  const colours = points.map((point, i) => slopeColour(slopeAt(points, i)));
  const last = points[points.length - 1].distance;
  const same = (a: [number, number, number] | undefined, b: [number, number, number]) => !!a && a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
  return points
    .map((point, i) => {
      const colour = colours[i];
      if (same(colours[i - 1], colour) && same(colours[i + 1], colour)) {
        return null;
      }
      return { offset: Math.round((last ? point.distance / last : 0) * 10000) / 10000, colour: `rgb(${colour.join(",")})` };
    })
    .filter((stop): stop is { offset: number; colour: string } => !!stop);
};

/**
 * What the colours of a profile mean, to go under one (this app's copy only):
 * without it a walk on the flat reads as hard, the drawing being stretched to
 * its box whatever the heights are.
 */
export function SlopeLegend({ className = "" }: { className?: string }) {
  return (
    <ul className={`grid grid-cols-2 gap-x-3 gap-y-1 text-[11.5px] text-ink-soft desk:flex desk:flex-wrap ${className}`}>
      {SLOPE_LABELS.map(([slope, label]) => (
        <li key={slope} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: `rgb(${slopeColour(slope).join(",")})` }} />
          {label}
        </li>
      ))}
    </ul>
  );
}

/**
 * The track's climbing as an SVG: distance walked across, height up the side,
 * the ground under the line filled in and coloured by how steep the climb is
 * there. Scales to whatever it is put in, having a viewBox and no size of its
 * own. No words: how far and how high are said beside it. Nothing for a track
 * with no heights.
 *
 * `at`, metres along the track, marks where someone is on it (the live trail
 * sheet; this app's copy only): what is still to walk is veiled, a line stands
 * at the place and a dot sits on the ground there. The dot is an element over
 * the drawing rather than part of it, since the drawing is stretched and a
 * circle in it would be too.
 */
export function ElevationProfile({
  track,
  title = "Perfil de elevação do percurso",
  className = "",
  at = null,
}: {
  track: TrackPoint[] | null | undefined;
  title?: string;
  className?: string;
  at?: number | null;
}) {
  const id = useId();
  const series = elevationSeries(track);
  if (series.length < 2) {
    return null;
  }

  const last = series[series.length - 1].distance;
  const heights = series.map((p) => p.elevation);
  const low = Math.min(...heights);
  let high = Math.max(...heights);
  if (high - low < MIN_RANGE) {
    high = low + MIN_RANGE;
  }

  const plot = { left: PADDING.left, right: WIDTH - PADDING.right, top: PADDING.top, bottom: HEIGHT - PADDING.bottom };
  const x = (distance: number) => plot.left + (last ? distance / last : 0) * (plot.right - plot.left);
  const y = (elevation: number) => plot.bottom - ((elevation - low) / (high - low)) * (plot.bottom - plot.top);
  const round = (value: number) => Math.round(value * 10) / 10;

  const points = columns(series, MAX_COLUMNS);
  const line = points.map((point, i) => `${i ? "L" : "M"}${round(x(point.distance))},${round(y(point.elevation))}`).join(" ");
  const area = `${line} L${round(x(last))},${plot.bottom} L${round(x(0))},${plot.bottom} Z`;
  const slopeId = `${id}-slope`;
  const fadeId = `${id}-fade`;

  // where `at` falls on the drawing: the height there is read between the two
  // points of the series it lies between
  let here: { x: number; y: number } | null = null;
  if (typeof at === "number" && Number.isFinite(at)) {
    const distance = Math.min(last, Math.max(0, at));
    const next = series.findIndex((point) => point.distance >= distance);
    const b = series[next < 0 ? series.length - 1 : next];
    const a = series[Math.max(0, (next < 0 ? series.length : next) - 1)];
    const part = b.distance > a.distance ? (distance - a.distance) / (b.distance - a.distance) : 0;
    here = { x: round(x(distance)), y: round(y(a.elevation + (b.elevation - a.elevation) * Math.min(1, Math.max(0, part)))) };
  }

  const drawing = (
    <svg className={`block w-full ${here ? "h-full" : `h-auto ${className}`}`} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" role="img" aria-label={title}>
      <defs>
        <linearGradient id={slopeId} gradientUnits="userSpaceOnUse" x1={plot.left} x2={plot.right} y1="0" y2="0">
          {slopeStops(points).map((stop, i) => (
            <stop key={i} offset={stop.offset} stopColor={stop.colour} />
          ))}
        </linearGradient>
        <linearGradient id={fadeId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="1" stopColor="#fff" stopOpacity="0.35" />
        </linearGradient>
      </defs>
      {gridLines(low, high).map((height) => (
        <line key={height} x1={plot.left} y1={round(y(height))} x2={plot.right} y2={round(y(height))} stroke={COLOURS.grid} strokeWidth="1" vectorEffect="non-scaling-stroke" />
      ))}
      <path d={area} fill={`url(#${slopeId})`} />
      <path d={area} fill={`url(#${fadeId})`} />
      <path d={line} fill="none" stroke={COLOURS.line} strokeOpacity="0.75" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      <line x1={plot.left} y1={plot.bottom} x2={plot.right} y2={plot.bottom} stroke={COLOURS.grid} strokeWidth="1" vectorEffect="non-scaling-stroke" />
      {here && (
        <>
          <rect x={here.x} y="0" width={Math.max(0, WIDTH - here.x)} height={HEIGHT} fill="#fff" fillOpacity="0.6" />
          <line x1={here.x} y1="0" x2={here.x} y2={HEIGHT} stroke={COLOURS.here} strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </>
      )}
    </svg>
  );

  if (!here) {
    return drawing;
  }
  return (
    <div className={`relative ${className}`}>
      {drawing}
      <span
        className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
        style={{ left: `${(here.x / WIDTH) * 100}%`, top: `${(here.y / HEIGHT) * 100}%`, background: COLOURS.here }}
      />
    </div>
  );
}
